// Requests that give up instead of waiting for ever. On a poor mobile connection a request can stall with no
// answer and no error; everything waiting on it (and its fallback to an older copy) would wait too.

/** How long a request may go without anything arriving before it counts as failed. */
export const QUIET_MS = 15e3;

/**
 * `fetch`, then `read` the response, failing if nothing arrives for `ms` (the wait for an answer, and every
 * pause in the download after it). A slow connection that is still downloading carries on; a stalled one
 * fails, so the caller's fallback takes over. The caller's own `signal` still cancels it.
 */
export function fetchWithin<T>(url: string, read: (r: Response) => Promise<T> | T, opts: RequestInit & { ms?: number } = {}): Promise<T> {
  const { ms = QUIET_MS, signal, ...init } = opts;
  const ctl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let done = false;
  let cancel = () => {};
  return new Promise<T>((resolve, reject) => {
    const fail = (e: unknown) => {
      ctl.abort(e);
      reject(e);
    };
    const wait = () => {
      clearTimeout(timer);
      if (!done) timer = setTimeout(() => fail(new DOMException('No answer in time', 'TimeoutError')), ms);
    };
    cancel = () => fail(signal?.reason ?? new DOMException('Cancelled', 'AbortError'));
    if (signal?.aborted) return cancel();
    signal?.addEventListener('abort', cancel, { once: true });
    wait();
    fetch(url, { ...init, signal: ctl.signal })
      .then((r) => {
        wait();
        if (!r.body) return read(r);
        // The same response, with each piece of the download putting the deadline back.
        const watch = new TransformStream({
          transform(chunk, out) {
            wait();
            out.enqueue(chunk);
          },
        });
        return read(new Response(r.body.pipeThrough(watch), { status: r.status, statusText: r.statusText, headers: r.headers }));
      })
      .then(resolve, reject);
  }).finally(() => {
    done = true;
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  });
}
